import { IFontProps } from "@/typings/Props";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import "aos/dist/aos.css";
import Link from "next/link";
import { media } from "@/styles/media";
import { theme } from "@/styles/theme";

type TOwnProps = {
  $backgroundImage?: string;
  $backgroundColor?: string;
  $fontColor?: string;
};

export default function Banner({ ...props }) {
  const { t } = useTranslation();

  // PDF 다운로드 함수
  const handleDownload = () => {
    const fileName = `${props.title}_catalog.pdf`;
    const filePath = `/download/${fileName}`;

    // 다운로드 링크 생성
    const link = document.createElement("a");
    link.href = filePath;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <BannerWrapper>
      <LeftArea>
        <TextWrapper data-aos="fade-up" data-aos-delay="500" data-aos-duration="1200">
          <P id="title" $fontColor={theme.colors.primary} $fontWeight="bold" $fontSize="76px">
            {t(props.title)}
          </P>
          <P id="desc" $fontColor={theme.colors.text.default} $fontWeight="bold" $fontSize="36px">
            {t(props.description)}
          </P>
          <P id="longDesc" $fontColor={theme.colors.text.grayDark} $lineHeight="24px">
            {t(props.longDesc)}
          </P>
          <ButtonWrapper>
            <LinkButton
              id="inquire"
              href="/contact/contact-us"
              $fontColor={theme.colors.text.white}
              $backgroundColor={theme.colors.button.black}
            >
              {t("products.button.inquire-today")}
            </LinkButton>
            <DownloadButton id="download" onClick={handleDownload} $fontColor={theme.colors.text.default}>
              {t("products.button.download-brochure")}
            </DownloadButton>
          </ButtonWrapper>
        </TextWrapper>
      </LeftArea>
      <RightArea>
        <ProductImage $backgroundImage={props.Image} data-aos="fade-right" data-aos-easing="linear" />
      </RightArea>
    </BannerWrapper>
  );
}

const BannerWrapper = styled.div`
  width: 100%;
  height: 1059px;
  display: flex;

  ${media.tablet`
    height: initial;
    flex-direction: column;
  `}
`;

const LeftArea = styled.div`
  height: 100%;
  padding: 13vw 7vw 6vw 7vw;
  flex: 1;

  ${media.tablet`
    padding: 20vw 7vw 6vw 7vw;
  `}
`;

const RightArea = styled.div`
  height: 100%;
  padding: 0 8vw;
  margin-top: 140px;
  flex: 1;

  ${media.tablet`
    width: 100%;
    margin-top: 80px;
  `}
`;

const TextWrapper = styled.div`
  padding: 0px;
`;

const P = styled.p<IFontProps>`
  width: 90%; //
  color: ${(props) => props.$fontColor};
  font-size: ${(props) => props.$fontSize};
  font-weight: ${(props) => props.$fontWeight};
  white-space: pre-wrap;
  line-height: ${(props) => (props.$lineHeight ? props.$lineHeight : "120%")};

  &#title {
    ${media.tablet`
      font-size: 60px; 
    `}
  }

  &#desc {
    width: 85%;
    margin-bottom: 32px;
    ${media.tablet`
      width: 90%;
    `}
  }
  &#longDesc {
    width: 93%;
    font-family: "Hind", sans-serif;
    ${media.tablet`
      width: 100%;
      padding: 0 48px 0 0;
    `}
  }

  @media (max-width: 1400px) {
    &#desc {
      font-size: 36px;
    }
  }
  ${media.tablet`
    margin: 0;
    padding: 0 80px 0 0;
    width: initial;
  `}
`;

const ProductImage = styled.div<TOwnProps>`
  width: 80%;
  height: 80%;
  background-image: url(${(props) => props.$backgroundImage});
  background-repeat: no-repeat;
  background-size: contain;
  background-position-x: center;

  ${media.tablet`
    width: 100%;
    min-height: 800px;
  `}
`;

const ButtonWrapper = styled.div`
  margin-top: 80px;
  display: flex;
  gap: 36px;

  ${media.tablet`
    margin: 40px 0 0 0;
    width: initial;
    height: 40px;
    `}
`;

const LinkButton = styled(Link)<TOwnProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.8em 2.2em;
  border: 1px solid black;
  border-radius: 50px;
  background-color: ${(props) => props.$backgroundColor};
  font-size: 1.1vw;
  color: ${(props) => props.$fontColor};
  transition: ease-out 0.3s;
  cursor: pointer;

  &:hover {
    transition: ease-out 0.3s;

    &#inquire {
      color: black;
      background: white;
    }
  }

  ${media.tablet`
    padding: 0px 30px;
    font-size: 13px;
  `}
`;

const DownloadButton = styled.button<TOwnProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.8em 2.2em;
  border: 1px solid black;
  border-radius: 50px;
  background-color: ${(props) => props.$backgroundColor || "transparent"};
  font-size: 1.1vw;
  color: ${(props) => props.$fontColor};
  transition: ease-out 0.3s;
  cursor: pointer;

  &:hover {
    transition: ease-out 0.3s;
    color: white;
    background: #971b2f;
  }

  ${media.tablet`
    padding: 0px 30px;
    font-size: 13px;
  `}
`;
